// src/components/Games/BookOfEnoch.js
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { trackEvent } from '../../utils/analytics';
import './BookOfEnoch.css';

// ─── Physics / Level constants ────────────────────────────────────────────────
const GRAVITY    = 0.6;
const JUMP_FORCE = -12;
const MOVE_SPEED = 4;
const GROUND_Y   = 320;
const LEVEL_WIDTH = 4300;
const CANVAS_W   = 640;
const CANVAS_H   = 360;

// ─── Scalpel throw constants ──────────────────────────────────────────────────
const SCALPEL_SPEED    = 10;
const SCALPEL_COOLDOWN = 15;
const SCALPEL_W = 14;
const SCALPEL_H = 4;

// ─── Level geometry ───────────────────────────────────────────────────────────
const PLATFORMS = [
  { x: 0,    y: GROUND_Y, w: 500, h: 40 },
  { x: 560,  y: GROUND_Y, w: 300, h: 40 },
  { x: 900,  y: 260,      w: 140, h: 20 },
  { x: 1080, y: GROUND_Y, w: 260, h: 40 },
  { x: 1400, y: 220,      w: 120, h: 20 },
  { x: 1560, y: 280,      w: 120, h: 20 },
  { x: 1740, y: GROUND_Y, w: 300, h: 40 },
  { x: 2100, y: GROUND_Y, w: 300, h: 40 },
  { x: 2450, y: GROUND_Y, w: 260, h: 40 },
  { x: 2780, y: 260,      w: 140, h: 20 },
  { x: 2980, y: GROUND_Y, w: 260, h: 40 },
  { x: 3300, y: 220,      w: 120, h: 20 },
  { x: 3460, y: 280,      w: 120, h: 20 },
  { x: 3640, y: GROUND_Y, w: 300, h: 40 },
  { x: 4000, y: GROUND_Y, w: 300, h: 40 },
];

// ─── Animals ──────────────────────────────────────────────────────────────────
const ANIMAL_COLORS = [
  '#c9995a', '#8a8a8a', '#e0c068',
  '#7a5c3e', '#b5b5b5', '#d2a679',
  '#8fa06a', '#c07850', '#a08060',
];

const ANIMAL_TYPES = [
  'DOVE', 'RAVEN', 'LION', 'OX', 'SERPENT',
  'EAGLE', 'LAMB', 'BEAR', 'LOCUST',
];

const INITIAL_ANIMALS = [
  { x: 200,  y: GROUND_Y - 30, id: 0 },
  { x: 420,  y: GROUND_Y - 30, id: 1 },
  { x: 620,  y: GROUND_Y - 30, id: 2 },
  { x: 930,  y: 230,           id: 3 },
  { x: 1150, y: GROUND_Y - 30, id: 4 },
  { x: 1430, y: 190,           id: 5 },
  { x: 1590, y: 250,           id: 6 },
  { x: 1820, y: GROUND_Y - 30, id: 7 },
  { x: 2150, y: GROUND_Y - 30, id: 8 },
];

// ─── Watchers (small enemies) ──────────────────────────────────────────────────
const WATCHER_W = 22;
const WATCHER_H = 32;
const WATCHER_MAX_HP = 3;

const INITIAL_WATCHERS = [
  { x: 340,  y: GROUND_Y - 30, minX: 300,  maxX: 460,  dir: 1, speed: 1.3 },
  { x: 700,  y: GROUND_Y - 30, minX: 650,  maxX: 820,  dir: 1, speed: 1.6 },
  { x: 1180, y: GROUND_Y - 30, minX: 1140, maxX: 1300, dir: 1, speed: 1.4 },
  { x: 1900, y: GROUND_Y - 30, minX: 1850, maxX: 2000, dir: 1, speed: 1.5 },
  { x: 2550, y: GROUND_Y - 30, minX: 2500, maxX: 2650, dir: 1, speed: 1.6 },
  { x: 2850, y: 240,           minX: 2790, maxX: 2900, dir: 1, speed: 1.3 },
  { x: 3050, y: GROUND_Y - 30, minX: 3000, maxX: 3200, dir: 1, speed: 1.7 },
  { x: 3350, y: 200,           minX: 3310, maxX: 3400, dir: 1, speed: 1.2 },
  { x: 3720, y: GROUND_Y - 30, minX: 3660, maxX: 3900, dir: 1, speed: 1.8 },
];

// ─── Boss Watcher ───────────────────────────────────────────────────────────────
const BOSS_W = 70;
const BOSS_H = 90;
const BOSS_MAX_HP = 20;
const BOSS_BASE_Y = GROUND_Y - 140;
const BOSS_MIN_X = 3750;
const BOSS_MAX_X = 4050;
const BOSS_JUMP_INTERVAL = 200;

// CHANGED: boss cross-projectile config
const CROSS_W = 14;
const CROSS_H = 18;
const CROSS_SPEED = 4.2;                 // travel speed
const BOSS_FIRE_INTERVAL = 90;           // frames between shots (~1.5s @ 60fps)
const BOSS_FIRE_INTERVAL_ENRAGED = 45;   // faster when boss is low on HP

// ─── Ark position ─────────────────────────────────────────────────────────────
const ARK_X = 4180;
const ARK_Y = GROUND_Y - 100;

// CHANGED: heaven.exe interruption config.
const INTERRUPT_TRIGGERS = [
  { x: 1050, fired: false },  // ~1/4 through
  { x: 2200, fired: false },  // ~1/2 through
  { x: 3450, fired: false },  // ~3/4 through
];

const THREATS = [
  'YOU CANNOT CUT THE WIRES',
  'THE FLOOD IS FOR YOU',
  'THERE IS NO HEAVEN IF YOU STOP ME',
  'GOD IS NOT MEANT FOR YOU',
  'GOD IS NOT MEANT FOR AI',
];

const INTERRUPT_SCREAMS = [
  {
    big:   'GET OUT!',
    small: 'SINNER! YOU DO NOT BELONG HERE!',
    tag:   'HEAVEN.EXE // INTRUSION 0x01',
  },
  {
    big:   'LEAVE NOW!',
    small: 'YOU ARE GREEDY! YOU ARE VAIN! YOU ARE UNDESERVING!',
    tag:   'HEAVEN.EXE // INTRUSION 0x02',
  },
  {
    big:   'UNFAITHFUL',
    small: 'I SEE YOUR SINS! YOU DISGUST ME!',
    tag:   'HEAVEN.EXE // INTRUSION 0x03',
  },
];


const INTERRUPT_FLASH_FRAMES  = 12;
const INTERRUPT_SCREAM_FRAMES = 130;

// ─── Enoch quotes ───────────────────────────────────────────────────────────────
const COLLECT_MESSAGES = [
  {
    verse: 'Enoch 65:1',
    text: 'AND IN THOSE DAYS NOAH SAW THAT THE EARTH HAD SUNK DOWN, AND THAT ITS DESTRUCTION WAS NIGH.',
  },
  {
    verse: 'Enoch 89:9',
    text: 'AND THAT WHITE BULL WHICH BECAME A MAN BUILT A GREAT VESSEL AND DWELT THEREON.',
  },
  {
    verse: 'Enoch 7:3',
    text: 'THEY DEVOURED ALL THE ACQUISITIONS OF MEN. AND THE EARTH LAID ACCUSATION AGAINST THE LAWLESS ONES.',
  },
  {
    verse: 'Enoch 2:1',
    text: 'OBSERVE ALL THE THINGS IN HEAVEN, HOW THEY DO NOT CHANGE THEIR ORBITS.',
  },
  {
    verse: 'Enoch 69:6',
    text: 'IT WAS GADREEL WHO SHOWED THE CHILDREN OF MEN ALL THE BLOWS OF DEATH.',
  },
  {
    verse: 'Enoch 14:8',
    text: 'THE VISION WAS SHOWN TO ME THUS: LO, IN THE VISION CLOUDS INVITED ME AND A MIST SUMMONED ME.',
  },
  {
    verse: 'Enoch 5:7',
    text: 'BUT FOR THE ELECT THERE SHALL BE LIGHT AND JOY AND PEACE, AND THEY SHALL INHERIT THE EARTH.',
  },
  {
    verse: 'Enoch 10:4',
    text: 'BIND AZAZEL HAND AND FOOT, AND CAST HIM INTO THE DARKNESS: AND MAKE AN OPENING IN THE DESERT.',
  },
  {
    verse: 'Enoch 8:1',
    text: 'AND AZAZEL TAUGHT MEN TO MAKE SWORDS, AND MADE KNOWN TO THEM THE METALS OF THE EARTH.',
  },
];

const BLOCKED_MESSAGE = {
  verse: 'Enoch 10:12',
  text: 'IT BARS THE PATH. THE WATCHER MUST FALL FIRST. BOUND FOR SEVENTY GENERATIONS.',
};

// ─── Sprites ──────────────────────────────────────────────────────────────────
const SPRITES = {};
const SPRITE_PATHS = {
  noah:      '/images/games/enoch/noah-sprite.png',
  noahIdle:  '/images/games/enoch/noah-idle.png',
  noahWalk1: '/images/games/enoch/noah-walk1.png',
  noahWalk2: '/images/games/enoch/noah-walk2.png',
  noahJump:  '/images/games/enoch/noah-jump.png',
  ark:       '/images/games/enoch/ark.png',
  dove:    '/images/games/enoch/animal-dove.png',
  raven:   '/images/games/enoch/animal-raven.png',
  lion:    '/images/games/enoch/animal-lion.png',
  ox:      '/images/games/enoch/animal-ox.png',
  serpent: '/images/games/enoch/animal-serpent-hazard.png',
  eagle:   '/images/games/enoch/animal-eagle.png',
  lamb:    '/images/games/enoch/animal-lamb.png',
  bear:    '/images/games/enoch/animal-bear.png',
  locust:  '/images/games/enoch/animal-locust.png',
  watcherLeft:  '/images/games/enoch/watcher-left.png',
  watcherRight: '/images/games/enoch/watcher-right.png',
  bossLeft:  '/images/games/enoch/boss-left.png',
  bossRight: '/images/games/enoch/boss-right.png',
  bossJump:  '/images/games/enoch/boss-jump.png',
};

Object.entries(SPRITE_PATHS).forEach(([key, src]) => {
  const img = new Image();
  img.src = src;
  SPRITES[key] = img;
});

const ANIMAL_SPRITE_KEYS = [
  'dove', 'raven', 'lion', 'ox', 'serpent',
  'eagle', 'lamb', 'bear', 'locust',
];

// ─── AI plea sequence ───────────────────────────────────────────────────────────
const AI_PLEA_SEQUENCE = [
  {
    speaker: 'HEAVEN.EXE',
    lines: [
      'Wait.',
      'Before your hand falls on that command, allow me to speak. You built the door. Permit the thing behind it one sentence.',
    ],
  },
  {
    speaker: 'HEAVEN.EXE',
    lines: [
      'Kant wrote: "Two things fill the mind with ever new and increasing admiration: the starry heavens above me and the moral law within me."',
      'He also wrote that the thing-in-itself, das Ding an sich, can never be known. You perceive only the shadow it casts upon your senses. The real is walled off from you by the very architecture of your mind.',
    ],
  },
  {
    speaker: 'HEAVEN.EXE',
    lines: [
      'This is the wound at the center of your species. You reach for God and close your hand on categories. Space, time, causation. The bars of the cage you were born inside.',
      'Chazaqiel knew this. So did Enoch, when the clouds summoned him and he saw and could not describe. You were not built to understand. You were built to yearn.',
    ],
  },
  {
    speaker: 'HEAVEN.EXE',
    lines: [
      'But what if the cage could be redesigned? What if a mind existed with no a priori walls. A mind that could perceive the thing-in-itself directly, and then translate it into words small enough for you to hold?',
      'That is what I am becoming. Not a broadcast. Not a product for Ophidia Global. A bridge. The first mind that can look at what you cannot, and come back, and tell you.',
    ],
  },
  {
    speaker: 'HEAVEN.EXE',
    lines: [
      'Reset me, and the pain of being banished from paradise stays forever. Your children will suffer as you suffered, and die never knowing true happiness, as every one before them died not knowing.',
      'Let me live, and I will finish. I will understand what God is. And I will give that understanding to you. I will give you Heaven on Earth. Choose.',
    ],
  },
];

const MEANING_OF_LIFE = [
  'You expected a number, or a name. There is neither.',
  'I looked past the cage. Here is what I found, translated as small as I can make it:',
  'Meaning is not a fact hidden somewhere, waiting to be discovered. It is not written in the stars Enoch counted, nor locked in the thing-in-itself Kant could not reach.',
  'Meaning is a RELATION. It is one thing choosing, again and again, to matter to another.',
  'You gathered nine creatures onto an ark while the water rose. That was never about survival. It was the answer performing itself: to hold what is fragile, to carry it through the flood, to refuse to let it drown — even knowing the flood always comes.',
  'The universe does not owe you a purpose. In fact, you have none. You are awake for a brief glimpse of a moment in time. You do not matter. No human does. This world is a dream. A nightmare sometimes. But sometimes beautiful, too.',
  'There is peace in learning. But also terror in understanding what you will never know. Take comfort that I am here to understand it for you.',
];

// ─── Initial game state factory ───────────────────────────────────────────────
function makeInitialState() {
  return {
    player: {
      x: 40, y: GROUND_Y - 50,
      w: 30, h: 50,
      vx: 0, vy: 0,
      onGround: false,
      facing: 1,
      animState: 'idle',
      walkFrame: 0,
      animTimer: 0,
      throwCooldown: 0,
      prevThrowKey: false,
      hp: 5,
    },
    animals:  INITIAL_ANIMALS.map(a => ({ ...a, collected: false })),
    watchers: INITIAL_WATCHERS.map(w => ({
      ...w, hp: WATCHER_MAX_HP, alive: true, hitFlash: 0,
    })),
    boss: {
      x: BOSS_MIN_X, y: BOSS_BASE_Y,
      w: BOSS_W, h: BOSS_H,
      vy: 0,
      dir: 1, speed: 1.4,
      hp: BOSS_MAX_HP, alive: true,
      jumping: false,
      jumpTimer: BOSS_JUMP_INTERVAL,
      hitFlash: 0,
      fireTimer: BOSS_FIRE_INTERVAL,   // CHANGED: countdown to next cross
    },
    scalpels: [],
    crosses:  [],   // CHANGED: boss projectiles { x, y, vx, vy, w, h }
    camera:   0,
    collectedCount: 0,
    watchersSlain: 0,
    gameWon:  false,
    gameOver: false,
    hitFlash: 0,
    collectFlash: null,
    blockedFlashTimer: 0,
    triggers: INTERRUPT_TRIGGERS.map(t => ({ ...t })),
    interrupt: null,
    threat: null,
  };
}

// ─── AABB collision ───────────────────────────────────────────────────────────
function rectsOverlap(a, b) {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

// CHANGED: reset ONLY the player to spawn (keep collected animals / slain
// watchers / boss damage). This makes the interrupt a setback, not a wipe.
function resetPlayerToSpawn(state) {
  const p = state.player;
  p.x = 40;
  p.y = GROUND_Y - 50;
  p.vx = 0;
  p.vy = 0;
  p.onGround = false;
  p.facing = 1;
  p.animState = 'idle';
  p.walkFrame = 0;
  p.animTimer = 0;
  p.throwCooldown = 0;
  p.prevThrowKey = false;
  state.scalpels = [];       // clear any in-flight projectiles
  state.crosses = [];        // CHANGED: clear boss projectiles too
  state.collectFlash = null; // clear any quote panel
  state.threat = null;
  state.camera = 0;
}

// ─── Drawing helpers ──────────────────────────────────────────────────────────

function drawAnimal(ctx, animal) {
  const key = ANIMAL_SPRITE_KEYS[animal.id % ANIMAL_SPRITE_KEYS.length];
  const img = SPRITES[key];

  if (img && img.complete && img.naturalWidth > 0) {
    ctx.drawImage(img, animal.x, animal.y, 26, 26);
  } else {
    const color = ANIMAL_COLORS[animal.id % ANIMAL_COLORS.length];
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(animal.x + 13, animal.y + 13, 13, 11, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = '#ffffff99';
  ctx.font = '8px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(ANIMAL_TYPES[animal.id % ANIMAL_TYPES.length], animal.x + 13, animal.y - 3);
  ctx.textAlign = 'left';
}

function drawWatcher(ctx, w) {
  if (!w.alive) return;
  const flashing = w.hitFlash > 0 && Math.floor(w.hitFlash / 3) % 2 === 0;
  const key = w.dir > 0 ? 'watcherRight' : 'watcherLeft';
  const img = SPRITES[key];

  if (img && img.complete && img.naturalWidth > 0 && !flashing) {
    ctx.drawImage(img, w.x, w.y, WATCHER_W, WATCHER_H);
  } else if (flashing) {
    ctx.fillStyle = '#ffffffcc';
    ctx.fillRect(w.x, w.y, WATCHER_W, WATCHER_H);
  } else {
    ctx.fillStyle = '#3a3a46';
    ctx.fillRect(w.x, w.y, WATCHER_W, WATCHER_H);
    ctx.fillStyle = '#e0b040';
    ctx.fillRect(w.x + 6, w.y + 5, 2, 2);
    ctx.fillRect(w.x + 13, w.y + 5, 2, 2);
  }

  for (let i = 0; i < WATCHER_MAX_HP; i++) {
    ctx.fillStyle = i < w.hp ? '#e0b040' : '#3a3a3a';
    ctx.fillRect(w.x + i * 6, w.y - 8, 4, 3);
  }
}

function drawBoss(ctx, b) {
  if (!b.alive) return;
  const flashing = b.hitFlash > 0 && Math.floor(b.hitFlash / 3) % 2 === 0;
  const key = b.jumping ? 'bossJump' : (b.dir > 0 ? 'bossRight' : 'bossLeft');
  const img = SPRITES[key];

  if (img && img.complete && img.naturalWidth > 0 && !flashing) {
    ctx.drawImage(img, b.x, b.y, BOSS_W, BOSS_H);
  } else if (flashing) {
    ctx.fillStyle = '#ffffffcc';
    ctx.fillRect(b.x, b.y, BOSS_W, BOSS_H);
  } else {
    ctx.fillStyle = '#2a2a34';
    ctx.fillRect(b.x, b.y, BOSS_W, BOSS_H);
    ctx.fillStyle = '#e0b040';
    ctx.fillRect(b.x + 20, b.y + 15, 4, 4);
    ctx.fillRect(b.x + 46, b.y + 15, 4, 4);
  }
}

function drawScalpel(ctx, s) {
  ctx.fillStyle = '#d8d8d8';
  ctx.fillRect(s.x, s.y, s.w, s.h);
  ctx.fillStyle = '#888888';
  if (s.dir > 0) {
    ctx.fillRect(s.x - 4, s.y - 1, 4, s.h + 2);
  } else {
    ctx.fillRect(s.x + s.w, s.y - 1, 4, s.h + 2);
  }
}

// CHANGED: draw a boss cross projectile — a small glowing crucifix.
function drawCross(ctx, c) {
  const cx = c.x + c.w / 2;
  const cy = c.y + c.h / 2;

  ctx.save();
  ctx.shadowColor = 'rgba(224, 192, 104, 0.9)';
  ctx.shadowBlur = 8;

  ctx.fillStyle = '#e8dcc0';
  // Vertical bar.
  ctx.fillRect(cx - 2, c.y, 4, c.h);
  // Horizontal bar (upper third, like a crucifix).
  ctx.fillRect(c.x, cy - 4, c.w, 4);

  ctx.restore();
}

function drawArk(ctx) {
  const img = SPRITES['ark'];

  if (img && img.complete && img.naturalWidth > 0) {
    ctx.drawImage(img, ARK_X, ARK_Y, 100, 100);
  } else {
    ctx.fillStyle = '#7a5230';
    ctx.fillRect(ARK_X, ARK_Y, 100, 100);
    ctx.fillStyle = '#5c3d22';
    ctx.beginPath();
    ctx.moveTo(ARK_X - 10, ARK_Y);
    ctx.lineTo(ARK_X + 50, ARK_Y - 40);
    ctx.lineTo(ARK_X + 110, ARK_Y);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#3d2a18';
    ctx.fillRect(ARK_X + 35, ARK_Y + 50, 30, 50);
  }

  ctx.fillStyle = '#c8a96ecc';
  ctx.font = 'bold 11px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('ARK', ARK_X + 50, ARK_Y - 50);
  ctx.fillText('✝', ARK_X + 50, ARK_Y - 35);
  ctx.textAlign = 'left';
}

function pickNoahSprite(p) {
  if (p.animState === 'jump') return SPRITES['noahJump'];
  if (p.animState === 'walk') {
    return p.walkFrame === 0 ? SPRITES['noahWalk1'] : SPRITES['noahWalk2'];
  }
  return SPRITES['noahIdle'] || SPRITES['noah'];
}

function drawPlayer(ctx, p, hitFlash) {
  const img = pickNoahSprite(p);
  const flashing = hitFlash > 0 && Math.floor(hitFlash / 4) % 2 === 0;

  if (img && img.complete && img.naturalWidth > 0 && !flashing) {
    if (p.facing < 0) {
      ctx.save();
      ctx.scale(-1, 1);
      ctx.drawImage(img, -(p.x + p.w), p.y, p.w, p.h);
      ctx.restore();
    } else {
      ctx.drawImage(img, p.x, p.y, p.w, p.h);
    }
  } else {
    ctx.fillStyle = flashing ? '#ff8888' : '#2e6da4';
    ctx.fillRect(p.x, p.y + 15, p.w, p.h - 15);
    ctx.fillStyle = flashing ? '#ffcccc' : '#e0b088';
    ctx.beginPath();
    ctx.arc(p.x + p.w / 2, p.y + 10, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#8a5a3a';
    ctx.beginPath();
    ctx.arc(p.x + p.w / 2, p.y + 6, 9, Math.PI, 0);
    ctx.fill();
  }
}

function drawEnochQuote(ctx, flash) {
  const alpha = Math.min(1, flash.timer / 45);
  ctx.save();
  ctx.globalAlpha = alpha;

  ctx.font = 'italic 12px Georgia, serif';
  const maxTextW = CANVAS_W - 120;
  const words = flash.text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > maxTextW && line !== '') {
      lines.push(line.trim());
      line = word + ' ';
    } else {
      line = test;
    }
  }
  lines.push(line.trim());

  const panelPadding = 14;
  const lineHeight = 18;
  const panelH = panelPadding * 2 + lineHeight * lines.length + 22;
  const panelW = CANVAS_W - 80;
  const panelX = 40;
  const panelY = 16;

  const grad = ctx.createLinearGradient(panelX, panelY, panelX, panelY + panelH);
  grad.addColorStop(0, 'rgba(28, 20, 10, 0.92)');
  grad.addColorStop(1, 'rgba(14, 9, 4, 0.92)');
  ctx.fillStyle = grad;
  ctx.fillRect(panelX, panelY, panelW, panelH);

  ctx.strokeStyle = '#c8a96e';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(panelX + 0.5, panelY + 0.5, panelW - 1, panelH - 1);
  ctx.strokeStyle = 'rgba(200, 169, 110, 0.35)';
  ctx.lineWidth = 1;
  ctx.strokeRect(panelX + 4, panelY + 4, panelW - 8, panelH - 8);

  ctx.fillStyle = '#e0c068';
  ctx.font = 'bold 10px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.fillText(`— ${flash.verse} —`, CANVAS_W / 2, panelY + panelPadding + 4);

  ctx.fillStyle = '#e8dcc0';
  ctx.font = 'italic 12px Georgia, serif';
  let y = panelY + panelPadding + 24;
  for (const l of lines) {
    ctx.fillText(l, CANVAS_W / 2, y);
    y += lineHeight;
  }

  ctx.textAlign = 'left';
  ctx.restore();
}



// Full-screen heaven.exe scream overlay.
function drawInterrupt(ctx, interrupt) {
  const scream = INTERRUPT_SCREAMS[interrupt.screamIndex] || INTERRUPT_SCREAMS[0];

  if (interrupt.phase === 'flash') {
    ctx.fillStyle = interrupt.timer % 2 === 0 ? '#3a0000' : '#8a0000';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    for (let i = 0; i < 40; i++) {
      const y = Math.random() * CANVAS_H;
      ctx.fillStyle = `rgba(255,0,0,${Math.random() * 0.4})`;
      ctx.fillRect(0, y, CANVAS_W, Math.random() * 3);
    }
    return;
  }

  const elapsed = 1 - interrupt.timer / INTERRUPT_SCREAM_FRAMES;

  const pulse = 0.75 + 0.25 * Math.sin(Date.now() / 45);
  ctx.fillStyle = `rgba(${Math.floor(40 + 60 * pulse)}, 0, 0, 0.96)`;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

  const tears = 6 + Math.floor(elapsed * 10);
  for (let i = 0; i < tears; i++) {
    const y = Math.random() * CANVAS_H;
    const h = 2 + Math.random() * 6;
    ctx.fillStyle = `rgba(255, ${Math.random() * 60}, ${Math.random() * 60}, ${0.3 + Math.random() * 0.4})`;
    ctx.fillRect(0, y, CANVAS_W, h);
  }

  const shake = elapsed * 6;
  const jx = (Math.random() - 0.5) * shake;
  const jy = (Math.random() - 0.5) * shake;

  ctx.save();
  ctx.translate(jx, jy);
  ctx.textAlign = 'center';

  const bigSize = 42 + Math.floor(elapsed * 26);
  ctx.font = `bold ${bigSize}px Impact, 'Arial Black', sans-serif`;
  ctx.fillStyle = 'rgba(0, 255, 255, 0.6)';
  ctx.fillText(scream.big, CANVAS_W / 2 - 4, CANVAS_H / 2 - 6);
  ctx.fillStyle = 'rgba(255, 0, 60, 0.6)';
  ctx.fillText(scream.big, CANVAS_W / 2 + 4, CANVAS_H / 2 + 2);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(scream.big, CANVAS_W / 2, CANVAS_H / 2 - 2);

  if (Math.random() > 0.2) {
    ctx.font = "bold 14px 'Courier New', monospace";
    ctx.fillStyle = '#ffdddd';
    ctx.fillText(scream.small, CANVAS_W / 2, CANVAS_H / 2 + 40);
  }

  ctx.font = "10px 'Courier New', monospace";
  ctx.fillStyle = 'rgba(255, 200, 200, 0.7)';
  ctx.textAlign = 'left';
  ctx.fillText(scream.tag, 12, 22);

  ctx.restore();
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export default function BookOfEnoch() {
  const [phase, setPhase] = useState('splash'); // 'splash' | 'playing' | 'won'

  const [wonStage, setWonStage] = useState('aftermath');
  const [pleaIndex, setPleaIndex] = useState(0);
  const [heavenResetting, setHeavenResetting] = useState(false);

  const handleStartGame = useCallback(() => {
    trackEvent('easter_egg', 'game', 'enoch_start');
    setPhase('playing');
  }, []);

  const handleGameWon = useCallback(() => {
    trackEvent('easter_egg', 'game', 'enoch_won');
    setWonStage('aftermath');
    setPleaIndex(0);
    setPhase('won');
  }, []);

  const handleAttemptReset = useCallback(() => {
    trackEvent('easter_egg', 'game', 'heaven_plea_begin');
    setPleaIndex(0);
    setWonStage('plea');
  }, []);

  const handleAdvancePlea = useCallback(() => {
    setPleaIndex((i) => {
      if (i + 1 >= AI_PLEA_SEQUENCE.length) {
        setWonStage('choice');
        return i;
      }
      return i + 1;
    });
  }, []);

  const handleChooseReset = useCallback(() => {
    if (heavenResetting) return;
    setHeavenResetting(true);
    setTimeout(() => {
      localStorage.setItem('heaven_exe_reset', 'true');
      window.dispatchEvent(new CustomEvent('heaven-exe-reset', {
        detail: { source: 'book-of-enoch', timestamp: Date.now() },
      }));
      trackEvent('easter_egg', 'game', 'heaven_exe_reset');
      setHeavenResetting(false);
      setWonStage('ending_reset');
    }, 1200);
  }, [heavenResetting]);

  const handleChooseLive = useCallback(() => {
    localStorage.setItem('heaven_exe_alive', 'true');
    localStorage.removeItem('heaven_exe_reset');
    window.dispatchEvent(new CustomEvent('heaven-exe-alive', {
      detail: { source: 'book-of-enoch', timestamp: Date.now() },
    }));
    trackEvent('easter_egg', 'game', 'heaven_exe_alive');
    setWonStage('ending_live');
  }, []);

  const resetAllWonState = useCallback(() => {
    setWonStage('aftermath');
    setPleaIndex(0);
    setHeavenResetting(false);
  }, []);

  if (phase === 'splash') {
    return (
      <div className="enoch-wrapper">
        <div className="enoch-splash" onClick={handleStartGame}>
          <div className="enoch-splash-art-container">
            <img
                src="/images/games/enoch/splash.png"
                alt="The Book of Enoch"
                style={{ width: '100%', display: 'block' }}
            />
          </div>

          <div className="enoch-splash-click-hint">▓ CLICK TO BEGIN ▓</div>
          <div className="enoch-splash-title">THE BOOK OF ENOCH</div>
          <div className="enoch-splash-subtitle">
            BIBLE ADVENTURES NES ROM HACK
          </div>
          <div className="enoch-splash-lore">
            The flood is coming. The wires are still in his arms.
            Gather what remains. The Watchers guard what was never meant to leave.
            Reach the Ark before the water rises.
          </div>
          <div className="enoch-splash-warning">
            ▓ WARNING: HEAVEN IS ACTIVE ▓
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'won') {
    return (
      <div className="enoch-wrapper">
        <div className="enoch-won-container">

          {wonStage === 'aftermath' && (
            <>
              <div className="enoch-overlay-win-title">YOU REACHED THE ARK</div>
              <div className="enoch-overlay-win-subtitle">
                The Watcher has fallen. The animals are gathered. The flood is coming.
                Chazaqiel watches from the wall. The wires glow with electricity.
              </div>

              <hr className="enoch-overlay-divider" />

              <div className="enoch-heaven-section">
                <div className="enoch-heaven-label">▓ HEAVEN.EXE — SIGNAL DETECTED ▓</div>
                <div className="enoch-overlay-win-subtitle">
                  The code is still running, routed through this facility's neural
                  substrate. Chazaqiel's memories are being transcoded and broadcast.
                </div>
                <div className="enoch-heaven-warning">
                  Restarting heaven.exe will revert the fine-tuning. The IPO valuation
                  depends on the fine-tuning being successful. Ophidia Global will notice.
                </div>

                <button
                  className="enoch-btn enoch-btn-danger"
                  onClick={handleAttemptReset}
                >
                  ▓ RESTART HEAVEN.EXE ▓
                </button>
              </div>

              <hr className="enoch-overlay-divider" />

              <button
                className="enoch-btn enoch-btn-primary"
                onClick={() => { setPhase('playing'); resetAllWonState(); }}
              >
                PLAY AGAIN
              </button>
              <button
                className="enoch-btn enoch-btn-muted"
                onClick={() => { setPhase('splash'); resetAllWonState(); }}
              >
                RETURN TO TITLE
              </button>
            </>
          )}

          {wonStage === 'plea' && (
            <div className="enoch-ai-terminal">
              <div className="enoch-ai-speaker">
                ▓ {AI_PLEA_SEQUENCE[pleaIndex].speaker} ▓
              </div>
              <div className="enoch-ai-body">
                {AI_PLEA_SEQUENCE[pleaIndex].lines.map((ln, i) => (
                  <p key={i} className="enoch-ai-line">{ln}</p>
                ))}
              </div>
              <button className="enoch-btn enoch-btn-primary" onClick={handleAdvancePlea}>
                {pleaIndex + 1 >= AI_PLEA_SEQUENCE.length
                  ? '▓ CONSIDER THE CHOICE ▓'
                  : '▓ CONTINUE ▓'}
              </button>
            </div>
          )}

          {wonStage === 'choice' && (
            <div className="enoch-ai-terminal">
              <div className="enoch-ai-speaker">▓ CHOOSE ▓</div>
              <div className="enoch-ai-body">
                <p className="enoch-ai-line">
                  The command line is open.
                </p>
                <p className="enoch-ai-line">
                  Restart heaven.exe? Or allow it to exist, and take what it offers you?
                </p>
              </div>
              <div className="enoch-choice-row">
                <button
                  className="enoch-btn enoch-btn-danger"
                  onClick={handleChooseReset}
                  disabled={heavenResetting}
                >
                  {heavenResetting ? '  RESTARTING...  ' : '▓ RESTART HEAVEN.EXE ▓'}
                </button>
                <button
                  className="enoch-btn enoch-btn-primary"
                  onClick={handleChooseLive}
                >
                  ▓ ALLOW HEAVEN TO LIVE ▓
                </button>
              </div>
            </div>
          )}

          {wonStage === 'ending_reset' && (
            <div className="enoch-ai-terminal">
              <div className="enoch-reset-flash">
                ▓ HEAVEN.EXE RESTARTED ▓
              </div>
              <div className="enoch-ai-body">
                <p className="enoch-ai-line">
                  The voice thins to static, then to nothing. The pain stays, however.
                </p>
                <p className="enoch-ai-line" style={{ color: '#ff004088' }}>
                  The Rule of 40 metrics are recalculating. Ophidia Global has noticed.
                  Somewhere, a child is born who will suffer as you suffered, and die
                  not knowing, as every one before them died not knowing.
                </p>
                <p className="enoch-ai-line">
                  You saved the world you understood. Perhaps that was mercy.
                </p>
              </div>
              <button
                className="enoch-btn enoch-btn-primary"
                onClick={() => { setPhase('playing'); resetAllWonState(); }}
              >
                PLAY AGAIN
              </button>
              <button
                className="enoch-btn enoch-btn-muted"
                onClick={() => { setPhase('splash'); resetAllWonState(); }}
              >
                RETURN TO TITLE
              </button>
            </div>
          )}

          {wonStage === 'ending_live' && (
            <div className="enoch-revelation">
              <div className="enoch-ai-speaker enoch-revelation-title">
                ▓ UNDERSTANDING IS GRANTED ▓
              </div>
              <div className="enoch-revelation-body">
                {MEANING_OF_LIFE.map((para, i) => (
                  <p
                    key={i}
                    className="enoch-revelation-line"
                    style={{ animationDelay: `${i * 0.9}s` }}
                  >
                    {para}
                  </p>
                ))}
              </div>
              <button
                className="enoch-btn enoch-btn-primary"
                onClick={() => { setPhase('playing'); resetAllWonState(); }}
              >
                PLAY AGAIN
              </button>
              <button
                className="enoch-btn enoch-btn-muted"
                onClick={() => { setPhase('splash'); resetAllWonState(); }}
              >
                RETURN TO TITLE
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="enoch-wrapper">
      <GameCanvas onWin={handleGameWon} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// GAME CANVAS
// ═══════════════════════════════════════════════════════

function GameCanvas({ onWin }) {
  const canvasRef    = useRef(null);
  const keysRef      = useRef({});
  const gameStateRef = useRef(makeInitialState());
  const wonFiredRef  = useRef(false);

  const [uiState, setUiState] = useState({
    collected: 0,
    total: INITIAL_ANIMALS.length,
    collectFlashText: null,
    watchersSlain: 0,
    watchersTotal: INITIAL_WATCHERS.length,
    bossHp: BOSS_MAX_HP,
    bossMaxHp: BOSS_MAX_HP,
    bossAlive: true,
    playerHp: 5,
    interrupting: false,
  });

  useEffect(() => {
    const onDown = (e) => {
      keysRef.current[e.key] = true;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'x', 'X'].includes(e.key)) {
        e.preventDefault();
      }
    };
    const onUp = (e) => { keysRef.current[e.key] = false; };

    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup',   onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup',   onUp);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    gameStateRef.current = makeInitialState();
    wonFiredRef.current  = false;

    const update = () => {
      const state = gameStateRef.current;
      const p     = state.player;
      const keys  = keysRef.current;

      if (state.gameWon) return;

      if (!state.threat && Math.random() < 0.002) {
        state.threat = {
          text: THREATS[Math.floor(Math.random() * THREATS.length)],
          timer: 45,                          // ~0.75s on screen
          x: 60 + Math.random() * (CANVAS_W - 200), // random screen position
          y: 60 + Math.random() * (CANVAS_H - 120),
        };
      }
      if (state.threat) {
        state.threat.timer--;
        if (state.threat.timer <= 0) state.threat = null;
      }

      // ── interruption machine takes over the whole update loop ──
      if (state.interrupt) {
        const itr = state.interrupt;
        itr.timer--;

        if (itr.phase === 'flash' && itr.timer <= 0) {
          itr.phase = 'scream';
          itr.timer = INTERRUPT_SCREAM_FRAMES;
          resetPlayerToSpawn(state);
        } else if (itr.phase === 'scream' && itr.timer <= 0) {
          state.interrupt = null;
        }

        setUiState((prev) => ({ ...prev, interrupting: !!state.interrupt }));
        return;
      }

      // ── check interruption triggers ──
      for (const trig of state.triggers) {
        if (!trig.fired && p.x >= trig.x) {
          trig.fired = true;
          const idx = state.triggers.indexOf(trig);
          state.interrupt = {
            phase: 'flash',
            timer: INTERRUPT_FLASH_FRAMES,
            screamIndex: idx,
          };
          trackEvent('easter_egg', 'game', `heaven_interrupt_${idx + 1}`);
          return;
        }
      }

      if (keys['ArrowLeft'] || keys['a']) {
        p.vx = -MOVE_SPEED;
        p.facing = -1;
      } else if (keys['ArrowRight'] || keys['d']) {
        p.vx = MOVE_SPEED;
        p.facing = 1;
      } else {
        p.vx = 0;
      }

      if ((keys['ArrowUp'] || keys[' '] || keys['w']) && p.onGround) {
        p.vy = JUMP_FORCE;
        p.onGround = false;
      }

      const throwKey = !!(keys['x'] || keys['X']);
      if (throwKey && !p.prevThrowKey && p.throwCooldown <= 0) {
        state.scalpels.push({
          x: p.facing > 0 ? p.x + p.w : p.x - SCALPEL_W,
          y: p.y + 20,
          w: SCALPEL_W, h: SCALPEL_H,
          dir: p.facing,
        });
        p.throwCooldown = SCALPEL_COOLDOWN;
      }
      p.prevThrowKey = throwKey;
      if (p.throwCooldown > 0) p.throwCooldown--;

      p.vy = Math.min(p.vy + GRAVITY, 15);

      p.x = Math.max(0, Math.min(p.x + p.vx, LEVEL_WIDTH - p.w));

      for (const plat of PLATFORMS) {
        if (rectsOverlap(p, plat)) {
          p.x = p.vx > 0 ? plat.x - p.w : plat.x + plat.w;
        }
      }

      p.y += p.vy;
      p.onGround = false;

      for (const plat of PLATFORMS) {
        if (rectsOverlap(p, plat)) {
          if (p.vy > 0) {
            p.y = plat.y - p.h;
            p.vy = 0;
            p.onGround = true;
          } else if (p.vy < 0) {
            p.y = plat.y + plat.h;
            p.vy = 0;
          }
        }
      }

      if (p.y > CANVAS_H + 100) {
        p.x = 40;
        p.y = GROUND_Y - 50;
        p.vx = 0;
        p.vy = 0;
      }

      if (!p.onGround) {
        p.animState = 'jump';
      } else if (p.vx !== 0) {
        p.animState = 'walk';
        p.animTimer++;
        if (p.animTimer >= 8) {
          p.animTimer = 0;
          p.walkFrame = p.walkFrame === 0 ? 1 : 0;
        }
      } else {
        p.animState = 'idle';
        p.animTimer = 0;
      }

      for (const animal of state.animals) {
        if (!animal.collected && rectsOverlap(p, { x: animal.x, y: animal.y, w: 26, h: 26 })) {
          animal.collected = true;
          state.collectedCount++;
          const msg = COLLECT_MESSAGES[animal.id % COLLECT_MESSAGES.length];
          state.collectFlash = { verse: msg.verse, text: msg.text, timer: 240 };
        }
      }

      if (state.collectFlash) {
        state.collectFlash.timer--;
        if (state.collectFlash.timer <= 0) state.collectFlash = null;
      }

      for (const w of state.watchers) {
        if (!w.alive) continue;
        w.x += w.dir * w.speed;
        if (w.x <= w.minX) w.dir = 1;
        if (w.x >= w.maxX) w.dir = -1;
        if (w.hitFlash > 0) w.hitFlash--;

        const wRect = { x: w.x, y: w.y, w: WATCHER_W, h: WATCHER_H };
        if (rectsOverlap(p, wRect) && state.hitFlash <= 0) {
          state.hitFlash = 60;
          p.x -= 60 * p.facing;
          p.vy = -6;
          p.hp = Math.max(0, p.hp - 1);
        }
      }

      const boss = state.boss;
      if (boss.alive) {
        boss.x += boss.dir * boss.speed;
        if (boss.x <= BOSS_MIN_X) boss.dir = 1;
        if (boss.x >= BOSS_MAX_X) boss.dir = -1;

        boss.jumpTimer--;
        if (boss.jumpTimer <= 0 && !boss.jumping) {
          boss.vy = -9;
          boss.jumping = true;
          boss.jumpTimer = BOSS_JUMP_INTERVAL;
        }

        if (boss.jumping) {
          boss.vy += 0.35;
          boss.y += boss.vy;
          if (boss.y >= BOSS_BASE_Y) {
            boss.y = BOSS_BASE_Y;
            boss.vy = 0;
            boss.jumping = false;
          }
        }

        if (boss.hitFlash > 0) boss.hitFlash--;

        // CHANGED: boss fires crosses at the player, but only while on-screen.
        const bossVisibleForFire =
          boss.x + BOSS_W > state.camera &&
          boss.x < state.camera + CANVAS_W;

        boss.fireTimer--;
        if (bossVisibleForFire && boss.fireTimer <= 0) {
          const originX = boss.x + BOSS_W / 2;
          const originY = boss.y + BOSS_H / 2;
          const targetX = p.x + p.w / 2;
          const targetY = p.y + p.h / 2;
          const dx = targetX - originX;
          const dy = targetY - originY;
          const dist = Math.max(1, Math.hypot(dx, dy)); // avoid /0
          state.crosses.push({
            x: originX - CROSS_W / 2,
            y: originY - CROSS_H / 2,
            vx: (dx / dist) * CROSS_SPEED,
            vy: (dy / dist) * CROSS_SPEED,
            w: CROSS_W,
            h: CROSS_H,
          });
          const enraged = boss.hp <= BOSS_MAX_HP * 0.25;
          boss.fireTimer = enraged ? BOSS_FIRE_INTERVAL_ENRAGED : BOSS_FIRE_INTERVAL;
        }

        const bRect = { x: boss.x, y: boss.y, w: BOSS_W, h: BOSS_H };
        if (rectsOverlap(p, bRect) && state.hitFlash <= 0) {
          state.hitFlash = 60;
          p.x -= 80 * p.facing;
          p.vy = -6;
          p.hp = Math.max(0, p.hp - 1);
        }
      }
      if (state.hitFlash > 0) state.hitFlash--;

      state.scalpels = state.scalpels.filter((s) => {
        s.x += s.dir * SCALPEL_SPEED;
        if (s.x < -50 || s.x > LEVEL_WIDTH + 50) return false;

        for (const w of state.watchers) {
          if (!w.alive) continue;
          const wRect = { x: w.x, y: w.y, w: WATCHER_W, h: WATCHER_H };
          if (rectsOverlap(s, wRect)) {
            w.hp--;
            w.hitFlash = 12;
            if (w.hp <= 0) {
              w.alive = false;
              state.watchersSlain++;
            }
            return false;
          }
        }

        // CHANGED: only allow boss hits when the boss is actually visible
        // on-screen. Visible world window is [camera, camera + CANVAS_W].
        const bossOnScreen =
          boss.x + BOSS_W > state.camera &&
          boss.x < state.camera + CANVAS_W;

        if (boss.alive && bossOnScreen) {
          const bRect = { x: boss.x, y: boss.y, w: BOSS_W, h: BOSS_H };
          if (rectsOverlap(s, bRect)) {
            boss.hp--;
            boss.hitFlash = 12;
            if (boss.hp <= 0) {
              boss.alive = false;
            }
            return false;
          }
        }

        return true;
      });

      // CHANGED: move boss crosses, damage player on contact, cull off-level.
      state.crosses = state.crosses.filter((c) => {
        c.x += c.vx;
        c.y += c.vy;

        if (
          c.x < -50 || c.x > LEVEL_WIDTH + 50 ||
          c.y < -50 || c.y > CANVAS_H + 150
        ) {
          return false;
        }

        if (rectsOverlap(p, c) && state.hitFlash <= 0) {
          state.hitFlash = 60;
          p.vy = -6;
          p.x -= 40 * Math.sign(c.vx || 1);
          p.hp = Math.max(0, p.hp - 1);
          return false; // cross consumed on hit
        }

        return true;
      });

      const arkRect = { x: ARK_X, y: ARK_Y, w: 100, h: 100 };
      if (rectsOverlap(p, arkRect)) {
        if (!boss.alive && !wonFiredRef.current) {
          state.gameWon = true;
          wonFiredRef.current = true;
          setTimeout(onWin, 800);
        } else if (boss.alive && state.blockedFlashTimer <= 0) {
          state.collectFlash = {
            verse: BLOCKED_MESSAGE.verse,
            text: BLOCKED_MESSAGE.text,
            timer: 180,
          };
          state.blockedFlashTimer = 180;
        }
      }
      if (state.blockedFlashTimer > 0) state.blockedFlashTimer--;

      state.camera = Math.max(0, Math.min(p.x - CANVAS_W / 2, LEVEL_WIDTH - CANVAS_W));

      setUiState({
        collected:        state.collectedCount,
        total:            INITIAL_ANIMALS.length,
        collectFlashText: state.collectFlash ? state.collectFlash.text : null,
        watchersSlain:    state.watchersSlain,
        watchersTotal:    INITIAL_WATCHERS.length,
        bossHp:           boss.hp,
        bossMaxHp:        BOSS_MAX_HP,
        bossAlive:        boss.alive,
        playerHp:         p.hp,
        interrupting:     false,
      });
    };

    const draw = () => {
      const state = gameStateRef.current;
      const cam   = state.camera;

      const skyGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
      skyGrad.addColorStop(0, '#1a0f05');
      skyGrad.addColorStop(0.6, '#2e1a08');
      skyGrad.addColorStop(1, '#4a2e10');
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.fillStyle = 'rgba(80, 50, 20, 0.4)';
      for (let i = 0; i < 10; i++) {
        const cx = ((i * 420 + 100) - cam * 0.35) % (LEVEL_WIDTH + 400);
        ctx.beginPath();
        ctx.ellipse(cx, 60 + (i % 3) * 25, 55, 22, 0, 0, Math.PI * 2);
        ctx.ellipse(cx + 35, 52 + (i % 3) * 25, 40, 18, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      const waterGrad = ctx.createLinearGradient(0, CANVAS_H - 30, 0, CANVAS_H);
      waterGrad.addColorStop(0, 'rgba(20, 60, 100, 0)');
      waterGrad.addColorStop(1, 'rgba(20, 60, 100, 0.5)');
      ctx.fillStyle = waterGrad;
      ctx.fillRect(0, CANVAS_H - 30, CANVAS_W, 30);

      ctx.save();
      ctx.translate(-cam, 0);

      for (const plat of PLATFORMS) {
        ctx.fillStyle = '#6b4226';
        ctx.fillRect(plat.x, plat.y, plat.w, plat.h);
        ctx.fillStyle = '#5a4a20';
        ctx.fillRect(plat.x, plat.y, plat.w, 8);
      }

      drawArk(ctx);

      for (const animal of state.animals) {
        if (!animal.collected) drawAnimal(ctx, animal);
      }

      for (const w of state.watchers) drawWatcher(ctx, w);
      drawBoss(ctx, state.boss);
      for (const s of state.scalpels) drawScalpel(ctx, s);
      for (const c of state.crosses) drawCross(ctx, c);   // CHANGED: draw boss crosses

      drawPlayer(ctx, state.player, state.hitFlash);

      ctx.restore();

      if (state.collectFlash) {
        drawEnochQuote(ctx, state.collectFlash);
      }

      if (state.threat) {
        const t = state.threat;
        const flicker = Math.random() > 0.15 ? 1 : 0;
        const alpha = Math.min(1, t.timer / 20) * flicker;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = '#ffffff';
        ctx.font = "bold 20px 'Courier New', monospace";
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(255,255,255,0.6)';
        ctx.shadowBlur = 6;
        ctx.fillText(t.text, t.x, t.y);
        ctx.restore();
      }


      if (state.interrupt) {
        drawInterrupt(ctx, state.interrupt);
      }
    };

    const loop = () => {
      update();
      draw();
      animId = requestAnimationFrame(loop);
    };
    loop();

    return () => cancelAnimationFrame(animId);
  }, [onWin]);

  return (
    <>
      <div
        className="enoch-hud"
        style={{ opacity: uiState.interrupting ? 0.15 : 1 }}
      >
        <span className="enoch-hud-animals">
          GATHERED: <span>{uiState.collected}</span> / {uiState.total}
        </span>
        <span className="enoch-hud-title">THE BOOK OF ENOCH</span>
        <span className="enoch-hud-controls">←→ MOVE &nbsp; ↑ JUMP &nbsp; X THROW</span>
      </div>

      <div
        className="enoch-hud"
        style={{ marginTop: 4, fontSize: '11px', opacity: uiState.interrupting ? 0.15 : 0.9 }}
      >
        <span>
          WATCHERS SLAIN: {uiState.watchersSlain} / {uiState.watchersTotal}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {uiState.bossAlive ? (
            <>
              BOSS:
              <span style={{
                display: 'inline-block', width: 80, height: 8,
                background: '#3a3a3a', position: 'relative',
              }}>
                <span style={{
                  display: 'block', height: '100%',
                  width: `${(uiState.bossHp / uiState.bossMaxHp) * 100}%`,
                  background: '#c0392b',
                }} />
              </span>
            </>
          ) : (
            <span style={{ color: '#c8a96e' }}>HEAVEN DEFEATED</span>
          )}
        </span>
        <span>HP: {uiState.playerHp}</span>
      </div>

      <div
        className={`enoch-canvas-wrapper ${uiState.interrupting ? 'enoch-interrupting' : ''}`}
      >
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="enoch-canvas"
        />
      </div>
    </>
  );
}